import React, { useState, useEffect, useRef } from "react";
import "./LayoutRenderer.css";
import { LayoutStructureImageModel, LayoutStructureModel, LayoutStructureTextModel } from "../../models/BlockTransportSystemJsonModel";

interface Props {
  layout: LayoutStructureModel;
  setLayout: (layout: LayoutStructureModel) => void;
  backgroundImage?: string;
}

const LayoutRenderer: React.FC<Props> = ({
  layout: layout,
  setLayout: setLayout,
  backgroundImage }) => {
  const [selectedItem, setSelectedItem] = useState<{itemId:string, type:"text"|"image"}|null>(null);
  const [dragging, setDragging] = useState<{itemId:string, type:"text"|"image"}|null>(null);
  const [resizing, setResizing] = useState<{itemId:string, type:"text"|"image"}|null>(null);
  const [rotating, setRotating] = useState<{itemId:string, type:"text"|"image"}|null>(null);
  const [clipboardItem, setClipboardItem] = useState<{item:any, type:"text"|"image"}|null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);


  // 마우스 이동 처리
  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragging) {
      const { itemId, type } = dragging;
      const deltaX = e.movementX;
      const deltaY = e.movementY;
      if (type === "text") {
        setLayout({
          ...layout,
          texts: layout.texts.map(t => t.id === itemId ? { ...t, x: t.x + deltaX, y: t.y + deltaY } : t)
        });
      } else {
        setLayout({
          ...layout,
          images: layout.images.map(img => img.id === itemId ? { ...img, x: img.x + deltaX, y: img.y + deltaY } : img)
        });
      }
    }
    if (resizing) {
      const { itemId, type } = resizing;
      const deltaX = e.movementX;
      const deltaY = e.movementY;
      if (type === "text") {
        setLayout({
          ...layout,
          texts: layout.texts.map(t => t.id === itemId ? { ...t, width: t.width + deltaX, height: t.height + deltaY } : t)
        });
      } else {
        setLayout({
          ...layout,
          images: layout.images.map(img => img.id === itemId ? { ...img, width: img.width + deltaX, height: img.height + deltaY } : img)
        });
      }
    }
    if (rotating) {
      const { itemId, type } = rotating;
      const deltaX = e.movementX;
      if (type === "text") {
        setLayout({
          ...layout,
          texts: layout.texts.map(t => t.id === itemId ? { ...t, rotation: t.rotation + deltaX } : t)
        });
      } else {
        setLayout({
          ...layout,
          images: layout.images.map(img => img.id === itemId ? { ...img, rotation: img.rotation + deltaX } : img)
        });
      }
    }
  };


  const handleMouseUp = () => {
    setDragging(null);
    setResizing(null);
    setRotating(null);
  };

  // 키보드 이벤트 등록
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedItem) return;
      const { itemId, type } = selectedItem;

      if (e.key === "Delete") {
        setLayout({
          ...layout,
          texts: type === "text" ? layout.texts.filter(t => t.id !== itemId) : layout.texts,
          images: type === "image" ? layout.images.filter(img => img.id !== itemId) : layout.images
        });
        setSelectedItem(null);
      }

      if (["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].includes(e.key)) {
        const deltaX = e.key === "ArrowLeft" ? -5 : e.key === "ArrowRight" ? 5 : 0;
        const deltaY = e.key === "ArrowUp" ? -5 : e.key === "ArrowDown" ? 5 : 0;
        if (type === "text") {
          setLayout({
            ...layout,
            texts: layout.texts.map(t => t.id === itemId ? { ...t, x: t.x + deltaX, y: t.y + deltaY } : t)
          });
        } else {
          setLayout({
            ...layout,
            images: layout.images.map(img => img.id === itemId ? { ...img, x: img.x + deltaX, y: img.y + deltaY } : img)
          });
        }
      }

      if (e.key.toLowerCase() === "r") {
        if (type === "text") {
          setLayout({
            ...layout,
            texts: layout.texts.map(t => t.id === itemId ? { ...t, rotation: t.rotation + 5 } : t)
          });
        } else {
          setLayout({
            ...layout,
            images: layout.images.map(img => img.id === itemId ? { ...img, rotation: img.rotation + 5 } : img)
          });
        }
      }

      if (e.ctrlKey && e.key.toLowerCase() === "c") {
        if (type === "text") {
          const item = layout.texts.find(t => t.id === itemId);
          if (item) setClipboardItem({ item, type });
        } else {
          const item = layout.images.find(img => img.id === itemId);
          if (item) setClipboardItem({ item, type });
        }
      }

      if (e.ctrlKey && e.key.toLowerCase() === "v") {
        if (clipboardItem) {
          if (clipboardItem.type === "text") {
            const newText: LayoutStructureTextModel = {
              ...clipboardItem.item,
              id: `text-${Date.now()}`,
              x: clipboardItem.item.x + 20,
              y: clipboardItem.item.y + 20
            };
            setLayout({ ...layout, texts: [...layout.texts, newText] });
          } else {
            const newImage: LayoutStructureImageModel = {
              ...clipboardItem.item,
              id: `image-${Date.now()}`,
              x: clipboardItem.item.x + 20,
              y: clipboardItem.item.y + 20
            };
            setLayout({ ...layout, images: [...layout.images, newImage] });
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedItem, clipboardItem, layout, setLayout]);


  // 실제 캔버스 크기
  const canvasWidth = 1844;
  const canvasHeight = 1240;


  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const parentWidth = containerRef.current.parentElement?.clientWidth ?? window.innerWidth;
        const parentHeight = containerRef.current.parentElement?.clientHeight ?? window.innerHeight;

        // 가로/세로 비율 중 작은 값으로 scale 결정
        const scaleX = parentWidth / canvasWidth;
        const scaleY = parentHeight / canvasHeight;
        setScale(Math.min(scaleX, scaleY));
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
// Shift+휠 줌 컨트롤
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.shiftKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1; // 휠 방향에 따라 확대/축소
        setScale(prev => {
          const newScale = Math.min(Math.max(prev + delta, 0.1), 3); // 0.1~3 범위 제한
          return newScale;
        });
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, []);



  return (
    <div>
      <div style={{ marginBottom: "10px" }}>
        <label>
          Zoom: {(scale * 100).toFixed(0)}%
          <input
            type="range"
            min="0.1"
            max="1"
            step="0.1"
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
          />
        </label>
        <span style={{ marginLeft: "10px", fontSize: "12px" }}>
          (Shift+마우스 휠로도 조절 가능)
        </span>
      </div>
      <div
        style={{
          width: (canvasWidth) * scale,
          height: (canvasHeight) * scale,
          overflow: "hidden",
          border: "1px solid black",
        }}>
        <div
          ref={containerRef}
              style={{
                width: canvasWidth,
                height: canvasHeight,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid black",
              }}
          className="layout-container" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
          <div
            key={layout.id}
            className="layout"
            style={{ width: layout.width, height: layout.height }}
          >
            {/* Images */}
            {layout.images.map(img => (
              <div
                key={img.id}
                className={`image-box ${selectedItem?.itemId === img.id ? "selected" : ""}`}
                style={{
                  left: img.x,
                  top: img.y,
                  width: img.width,
                  height: img.height,
                  transform: `rotate(${img.rotation}deg)`
                }}
                onMouseDown={() => setDragging({ itemId: img.id, type: "image" })}
                onClick={() => setSelectedItem({ itemId: img.id, type: "image" })}
              >
                📷 {img.id}
                <div
                  className="resize-handle"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setResizing({ itemId: img.id, type: "image" });
                  }}
                />
                <div
                  className="rotate-handle"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setRotating({ itemId: img.id, type: "image" });
                  }}
                />
              </div>
            ))}

            {/* Texts */}
            {layout.texts.map(text => (
              <div
                key={text.id}
                className={`text-box ${selectedItem?.itemId === text.id ? "selected" : ""}`}
                style={{
                  left: text.x,
                  top: text.y,
                  width: text.width,
                  height: text.height,
                  transform: `rotate(${text.rotation}deg)`,
                  fontFamily: text.font.family,
                  fontSize: text.font.size,
                  color: text.font.color,
                  textAlign: text.horizontalAlign as any
                }}
                onMouseDown={() => setDragging({ itemId: text.id, type: "text" })}
                onClick={() => setSelectedItem({ itemId: text.id, type: "text" })}
              >
                {text.text}
                <div
                  className="resize-handle"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setResizing({ itemId: text.id, type: "text" });
                  }}
                />
                <div
                  className="rotate-handle"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setRotating({ itemId: text.id, type: "text" });
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
);

};

export default LayoutRenderer;
